<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>
        USER JOURNEY
        <small>Journey Detail</small>
      </h1>
      <ol class="breadcrumb">
        <li><router-link to="/"><i class="fa fa-dashboard"></i> Home</router-link></li>
        <li><router-link to="/user-journey/view">View User Journeys</router-link></li>
        <li class="active">{{ currentJourneyData.name }}</li>
      </ol>
    </section>
    <section class="content">
      <div class="box">
        <div class="box-body">
          <div class="journey-settings-header">
            <h3>{{ currentJourneyData.name }}</h3>
            <div class="journey-settings-meta">
              <span :class="['label', currentJourneyData.deployed ? 'label-success' : 'label-default']">
                {{ currentJourneyData.deployed ? 'Deployed' : 'Not deployed' }}
              </span>
              <span v-if="currentJourneyData.releasedDate">Last released: {{ formatDate(currentJourneyData.releasedDate) }}</span>
            </div>
          </div>

          <div class="journey-settings-tabs">
            <button
              type="button"
              class="journey-settings-tab"
              :class="{ 'journey-settings-tab--active': activeTab === 'funnel' }"
              @click="activeTab = 'funnel'">
              <i class="fa fa-filter"></i> Funnel Stage Mapping
            </button>
            <button
              type="button"
              class="journey-settings-tab"
              :class="{ 'journey-settings-tab--active': activeTab === 'nudge' }"
              @click="activeTab = 'nudge'">
              <i class="fa fa-bell-o"></i> Nudge Settings
            </button>
            <button
              type="button"
              class="journey-settings-tab"
              :class="{ 'journey-settings-tab--active': activeTab === 'metadata' }"
              @click="activeTab = 'metadata'">
              <i class="fa fa-info-circle"></i> Journey Metadata
            </button>
          </div>

          <div v-if="activeTab === 'funnel'" class="journey-settings-tab-content">
            <funnel-stage-mapping
              v-model="funnelStageMapping"
              :journey-name="currentJourneyData.name"
            />
          </div>
          <div v-else class="journey-settings-tab-content journey-settings-tab-content--placeholder">
            <i class="fa fa-wrench fa-2x"></i>
            <div>Coming soon.</div>
          </div>

          <div class="journey-settings-footer">
            <span class="text-muted">Changes apply to all future campaigns for this journey. Active campaigns are not affected.</span>
            <div>
              <button type="button" class="btn btn-default" @click="discardAndGoBack">Discard &amp; go back</button>
              <button type="button" class="btn btn-primary" :disabled="saving" @click="saveChanges">
                {{ saving ? 'Saving...' : 'Save changes' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
  </div>
</template>

<script>
import api from '../../../utils/api'
import FunnelStageMapping from './FunnelStageMapping.vue'

export default {
  name: 'journey-settings',
  components: {
    'funnel-stage-mapping': FunnelStageMapping
  },
  data () {
    return {
      loading: false,
      saving: false,
      activeTab: 'funnel',
      currentJourneyData: {},
      funnelStageMapping: []
    }
  },
  mounted () {
    this.retrieveAnApp(this.$route.query.app)
  },
  methods: {
    retrieveAnApp (appId) {
      this.loading = true
      api.buildRequest(`app/${appId}`, 'GET')
        .then(response => {
          if (response.status === 200) {
            this.currentJourneyData = response.data.payload.details
            this.loadJourneySettings()
          } else {
            this.loading = false
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    loadJourneySettings () {
      api.buildRequest('journey-settings', 'GET', { app: this.currentJourneyData.name })
        .then(response => {
          this.loading = false
          if (response.status === 200 && response.data.payload && response.data.payload.value) {
            this.funnelStageMapping = response.data.payload.value.funnel_stage_mapping || []
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      return isNaN(date.getTime()) ? value : date.toLocaleString()
    },
    discardAndGoBack () {
      this.$router.push('/user-journey/view')
    },
    async saveChanges () {
      this.saving = true
      try {
        const payload = {
          app: this.currentJourneyData.name,
          value: {
            funnel_stage_mapping: this.funnelStageMapping
          }
        }
        const response = await api.buildRequest('journey-settings', 'POST', payload)
        this.saving = false
        if (response.status === 200) {
          this.$swal({
            title: 'Journey settings saved',
            type: 'success'
          }).then(() => {
            this.$router.push('/user-journey/view')
          })
        } else {
          this.$swal({
            title: 'Could not save journey settings',
            type: 'error',
            text: (response.data && response.data.payload && response.data.payload.message) || 'Please try again.'
          })
        }
      } catch (error) {
        this.saving = false
        this.$swal({
          title: 'Could not save journey settings',
          type: 'error',
          text: (error.response && error.response.data && error.response.data.payload && error.response.data.payload.message) || 'Please try again.'
        })
      }
    }
  }
}
</script>

<style scoped>
.journey-settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.journey-settings-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #718096;
}
.journey-settings-tabs {
  display: flex;
  border-bottom: 2px solid #cbd5e0;
  margin-bottom: 20px;
}
.journey-settings-tab {
  padding: 10px 20px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #718096;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}
.journey-settings-tab--active {
  color: #0D9488;
  border-bottom-color: #0D9488;
}
.journey-settings-tab-content--placeholder {
  color: #a0aec0;
  padding: 60px 40px;
  text-align: center;
}
.journey-settings-tab-content--placeholder .fa {
  margin-bottom: 10px;
}
.journey-settings-tab .fa {
  margin-right: 6px;
}
.journey-settings-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
  padding-top: 15px;
  margin-top: 20px;
}
</style>



// WEBPACK FOOTER //
// JourneySettings.vue?2004b016